import { useForm } from '@refinedev/react-hook-form';
import type { HttpError } from '@refinedev/core';
import { useNavigate } from 'react-router';
import type { TfUserRow, UpdateUserBody } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface FormValues {
  name: string;
  avatarUrl: string;
  roles: string;
}

const fromUser = (u: Partial<TfUserRow>): FormValues => ({
  name: u.name ?? '',
  avatarUrl: u.avatarUrl ?? '',
  roles: (u.roles ?? []).join(', '),
});

/**
 * `FormValues` claims these are all strings, but refine seeds the form from the
 * fetched `TfUserRow`, so a field the user never edits arrives in its RECORD
 * shape, not its form shape: `avatarUrl` is `null` and `roles` is a `string[]`.
 * A bare `.trim()` on either throws, which crashed Save for every user without
 * an avatar — i.e. almost all of them — with the mutation never leaving the
 * browser and the form simply sitting there.
 *
 * So these two readers accept both shapes rather than trusting `FormValues`.
 * Regression pin: `qa/e2e/05_admin_spa/03_admin_spa_crud.ts` T-5.5/5.6.
 */
const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

const toRoles = (v: unknown): string[] => {
  // Untouched → the record's array. Edited → the comma-separated field.
  if (Array.isArray(v)) return v.filter((r): r is string => typeof r === 'string');
  return text(v)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const toBody = (v: FormValues): UpdateUserBody => ({
  name: text(v.name) === '' ? null : text(v.name),
  avatarUrl: text(v.avatarUrl) === '' ? null : text(v.avatarUrl),
  roles: toRoles(v.roles),
});

export function UsersEdit() {
  const navigate = useNavigate();
  const {
    refineCore: { onFinish, query },
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<TfUserRow, HttpError, FormValues>({
    refineCoreProps: { onMutationSuccess: () => navigate('/admin/users') },
  });

  const initial = query?.data?.data ? fromUser(query.data.data) : { name: '', avatarUrl: '', roles: '' };

  const submit = handleSubmit(async (values) => {
    await onFinish(toBody(values) as unknown as FormValues);
  });

  if (query?.isLoading) return <div>Loading…</div>;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Edit user</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" defaultValue={initial.name} {...register('name')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="avatarUrl">Avatar URL</Label>
              <Input
                id="avatarUrl"
                type="url"
                placeholder="https://…"
                defaultValue={initial.avatarUrl}
                {...register('avatarUrl')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="roles">Roles (comma separated)</Label>
              <Input
                id="roles"
                defaultValue={initial.roles}
                placeholder="admin, member"
                {...register('roles')}
              />
              <p className="text-xs text-muted-foreground">
                Use <code>admin</code> for system-wide admin access.
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/users')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
